import type {
  DccMarkupRateColumnFilterKey,
  DccMarkupRateColumnFilters,
  DccMarkupRateGridRow,
  DccMarkupRateRow,
} from '../models/types';
import { formatAuditDateTime, mapAuditFields } from '../../../lib/auditColumnDefs';
import { exportRowsToExcel } from '../../../utils/excelExport';
import { DCC_MARKUP_RATE_EXPORT_COLUMNS } from '../table/columnDefs';

export function mapDccMarkupRateRow(item: Record<string, unknown>): DccMarkupRateRow {
  return {
    id: Number(item.id),
    merchantTerminalId: Number(item.merchantTerminalId ?? 0),
    merchantTerminalName: String(item.merchantTerminalName ?? '—'),
    merchantId: Number(item.merchantId ?? 0),
    merchantName: String(item.merchantName ?? '—'),
    fromCurrency: String(item.fromCurrency ?? ''),
    toCurrency: String(item.toCurrency ?? ''),
    rate: Number(item.rate ?? 0),
    status: String(item.status ?? 'UNKNOWN'),
    notes: item.notes != null ? String(item.notes) : null,
    createdAt: item.createdAt ? String(item.createdAt) : null,
    ...mapAuditFields(item),
  };
}

export function getApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const message = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
    if (message) return message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function formatDccRateCreatedLabel(createdAt: string | null): string {
  if (!createdAt) return '—';
  return new Date(createdAt).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function buildDccMarkupRateGridRows(rates: DccMarkupRateRow[]): DccMarkupRateGridRow[] {
  return rates.map((rate) => ({
    ...rate,
    pair: `${rate.fromCurrency} → ${rate.toCurrency}`,
    rateLabel: `1 ${rate.fromCurrency} = ${rate.rate} ${rate.toCurrency}`,
    createdLabel: formatDccRateCreatedLabel(rate.createdAt),
  }));
}

export async function exportDccMarkupRatesExcel(rows: DccMarkupRateGridRow[]) {
  await exportRowsToExcel(
    rows.map((row) => ({
      pair: row.pair,
      rateLabel: row.rateLabel,
      notes: row.notes?.trim() || '—',
      createdAt: formatAuditDateTime(row.createdAt),
      status: row.status || '—',
    })),
    DCC_MARKUP_RATE_EXPORT_COLUMNS,
    {
      sheetName: 'DCC Markup Rates',
      fileNamePrefix: 'dcc-markup-rates',
    },
  );
}

const DCC_MARKUP_RATE_COLUMN_FILTER_KEYS: Record<DccMarkupRateColumnFilterKey, true> = {
  pair: true,
  rateLabel: true,
  status: true,
  notes: true,
  createdAt: true,
};

/**
 * Translate AG Grid's native column filterModel into the same shape the Filters drawer
 * stores in `columnFilters` — so header-funnel filters and the drawer stay in sync.
 *
 * Text filters → `{ [colId]: [filterText] }` (drawer multi-select uses string[] too).
 * Date column (`agDateColumnFilter` on createdAt) → `createdAt: [from, to]` yyyy-MM-dd.
 */
export function mapAgGridFilterModelToDccMarkupRateFilters(
  filterModel: Record<string, unknown> | null | undefined,
): Partial<DccMarkupRateColumnFilters> {
  const columnFilters: Partial<DccMarkupRateColumnFilters> = {};
  if (!filterModel) return columnFilters;

  for (const [colId, raw] of Object.entries(filterModel)) {
    if (!raw || typeof raw !== 'object') continue;
    const model = raw as {
      filter?: unknown;
      dateFrom?: string;
      dateTo?: string;
    };

    if (colId === 'createdAt') {
      if (typeof model.dateFrom === 'string' && model.dateFrom.length >= 10) {
        const from = model.dateFrom.slice(0, 10);
        const to = (model.dateTo ?? model.dateFrom).slice(0, 10);
        columnFilters.createdAt = [from, to];
      }
      continue;
    }

    if (typeof model.filter !== 'string' || !model.filter.trim()) continue;
    if (colId in DCC_MARKUP_RATE_COLUMN_FILTER_KEYS) {
      columnFilters[colId as DccMarkupRateColumnFilterKey] = [model.filter];
    }
  }

  return columnFilters;
}

function dccColumnFilterCell(
  row: DccMarkupRateGridRow,
  key: DccMarkupRateColumnFilterKey,
): string {
  switch (key) {
    case 'pair':
      return row.pair;
    case 'rateLabel':
      return row.rateLabel;
    case 'status':
      return row.status;
    case 'notes':
      return row.notes ?? '';
    case 'createdAt':
      return row.createdAt ?? '';
    default:
      return '';
  }
}

export function rowMatchesDccColumnFilters(
  row: DccMarkupRateGridRow,
  filters: DccMarkupRateColumnFilters,
): boolean {
  for (const key of Object.keys(DCC_MARKUP_RATE_COLUMN_FILTER_KEYS) as DccMarkupRateColumnFilterKey[]) {
    const values = filters[key];
    if (!values?.length) continue;

    if (key === 'createdAt') {
      const from = values[0];
      const to = values[1] ?? values[0];
      const d = row.createdAt?.slice(0, 10) ?? '';
      if (!d) return false;
      if (from && d < from) return false;
      if (to && d > to) return false;
      continue;
    }

    const cell = dccColumnFilterCell(row, key).toLowerCase();
    if (!values.some((value) => cell.includes(value.trim().toLowerCase()))) {
      return false;
    }
  }
  return true;
}
